import { injectable } from 'inversify';
import { makeAutoObservable } from 'mobx';

import {
  KomplyanceAiAssessmentCompletedChunk,
  KomplyanceAiAssessmentProgressChunk,
  KomplyanceAiAssessmentStartedChunk,
  KomplyanceAiFileIngestProgressChunk,
  KomplyanceAiInvalidChunk,
  KomplyanceAiMessageChunk,
  KomplyanceAiMessageEndChunk,
  KomplyanceAiMessageEndRetrieverResource,
  KomplyanceAiStepProgressChunk,
  KomplyanceAiWorkflowFinishedChunk,
  KomplyanceAiWorkflowStartedChunk,
} from '@libs/types';

export interface StreamCallbacks {
  onChunk?: (chunk: KomplyanceAiMessageChunk) => void;
  onMetadata?: (chunk: KomplyanceAiWorkflowStartedChunk) => void;
  onDocuments?: (chunk: KomplyanceAiMessageEndChunk) => void;
  onError?: (chunk: KomplyanceAiInvalidChunk) => void;
  onComplete?: (chunk: KomplyanceAiWorkflowFinishedChunk) => void;
  onFileIngestProgress?: (chunk: KomplyanceAiFileIngestProgressChunk) => void;
  onAssessmentStarted?: (chunk: KomplyanceAiAssessmentStartedChunk) => void;
  onAssessmentProgress?: (chunk: KomplyanceAiAssessmentProgressChunk) => void;
  onAssessmentCompleted?: (chunk: KomplyanceAiAssessmentCompletedChunk) => void;
  onStepProgress?: (chunk: KomplyanceAiStepProgressChunk) => void;
}

export interface StreamController {
  abortController: AbortController;
  abort: () => void;
}

export interface ActiveStream {
  conversationId: string;
  taskId?: string;
  isActive: boolean;
  controller: StreamController;
  streamingMessage: string;
  streamingReferences: KomplyanceAiMessageEndRetrieverResource[];
  streamingStage: string;
  streamingStageMessage: string;
}

@injectable()
export class StreamingStore {
  // Map of conversation ID to list of listeners (subscribers)
  listeners = new Map<string, StreamCallbacks[]>();
  // Map of conversation ID to active stream information
  streams = new Map<string, ActiveStream>();
  // Pending user message per conversation — cleared on stream complete/error
  pendingUserMessages = new Map<string, { content: string; createdAt: Date }>();

  constructor() {
    // Exclude listeners and streams from being observable
    // These are internal implementation details, not reactive UI state
    // Making them non-observable prevents issues when accessed from async/non-React contexts
    makeAutoObservable(this, {
      listeners: false,
      streams: true,
      pendingUserMessages: false,
      subscribe: false,
      notifyMetadataListeners: false,
      notifyErrorListeners: false,
      notifyCompleteListeners: false,
      notifyChunkListeners: false,
      notifyDocumentsListeners: false,
      notifyFileIngestProgressListeners: false,
      notifyAssessmentStartedListeners: false,
      notifyAssessmentProgressListeners: false,
      notifyAssessmentCompletedListeners: false,
      notifyStepProgressListeners: false,
    });
  }

  /**
   * Store a new active stream (called from API layer)
   */
  setActiveStream(
    conversationId: string,
    taskId: string | undefined,
    controller: StreamController,
  ): void {
    this.streams.set(conversationId, {
      conversationId,
      taskId,
      isActive: true,
      controller,
      streamingMessage: '',
      streamingReferences: [],
      streamingStage: '',
      streamingStageMessage: '',
    });
  }

  /**
   * Update stream status (called from API layer)
   */
  setStreamActive(conversationId: string, isActive: boolean): void {
    const stream = this.streams.get(conversationId);
    if (stream) {
      stream.isActive = isActive;
    }
  }

  /**
   * Append message chunk to streaming message
   */
  appendStreamingMessage(conversationId: string, message: string): void {
    const stream = this.streams.get(conversationId);
    if (stream) {
      stream.streamingMessage += message;
    }
  }

  /**
   * Set streaming references
   */
  setStreamingReferences(
    conversationId: string,
    references: KomplyanceAiMessageEndRetrieverResource[],
  ): void {
    const stream = this.streams.get(conversationId);
    if (stream) {
      stream.streamingReferences = references;
    }
  }

  /**
   * Set the current streaming stage (e.g. 'indexing', 'assessing')
   */
  setStreamingStage(conversationId: string, stage: string, stageMessage: string): void {
    const stream = this.streams.get(conversationId);
    if (stream) {
      stream.streamingStage = stage;
      stream.streamingStageMessage = stageMessage;
    }
  }

  /**
   * Get the current streaming stage for a conversation
   */
  getStreamingStage(conversationId: string): { stage: string; stageMessage: string } {
    const stream = this.streams.get(conversationId);
    return {
      stage: stream?.streamingStage ?? '',
      stageMessage: stream?.streamingStageMessage ?? '',
    };
  }

  /**
   * Clear streaming content on error
   */
  clearStreamingContent(conversationId: string): void {
    const stream = this.streams.get(conversationId);
    if (stream) {
      stream.streamingMessage = '';
      stream.streamingReferences = [];
      stream.streamingStage = '';
      stream.streamingStageMessage = '';
    }
  }

  notifyMetadataListeners(conversationId: string, chunk: KomplyanceAiWorkflowStartedChunk) {
    const listeners = this.listeners.get(conversationId);
    if (listeners) {
      listeners.forEach((listener) => listener.onMetadata?.(chunk));
    }
  }

  notifyErrorListeners(conversationId: string, chunk: KomplyanceAiInvalidChunk) {
    const listeners = this.listeners.get(conversationId);
    if (listeners) {
      listeners.forEach((listener) => listener.onError?.(chunk));
    }
  }

  notifyCompleteListeners(conversationId: string, chunk: KomplyanceAiWorkflowFinishedChunk) {
    const listeners = this.listeners.get(conversationId);
    if (listeners) {
      listeners.forEach((listener) => listener.onComplete?.(chunk));
    }
  }

  notifyChunkListeners(conversationId: string, chunk: KomplyanceAiMessageChunk) {
    const listeners = this.listeners.get(conversationId);
    if (listeners) {
      listeners.forEach((listener) => listener.onChunk?.(chunk));
    }
  }

  notifyDocumentsListeners(conversationId: string, chunk: KomplyanceAiMessageEndChunk) {
    const listeners = this.listeners.get(conversationId);
    if (listeners) {
      listeners.forEach((listener) => listener.onDocuments?.(chunk));
    }
  }

  notifyFileIngestProgressListeners(conversationId: string, chunk: KomplyanceAiFileIngestProgressChunk) {
    const listeners = this.listeners.get(conversationId);
    if (listeners) {
      listeners.forEach((listener) => listener.onFileIngestProgress?.(chunk));
    }
  }

  notifyAssessmentStartedListeners(conversationId: string, chunk: KomplyanceAiAssessmentStartedChunk) {
    const listeners = this.listeners.get(conversationId);
    if (listeners) {
      listeners.forEach((listener) => listener.onAssessmentStarted?.(chunk));
    }
  }

  notifyAssessmentProgressListeners(conversationId: string, chunk: KomplyanceAiAssessmentProgressChunk) {
    const listeners = this.listeners.get(conversationId);
    if (listeners) {
      listeners.forEach((listener) => listener.onAssessmentProgress?.(chunk));
    }
  }

  notifyAssessmentCompletedListeners(conversationId: string, chunk: KomplyanceAiAssessmentCompletedChunk) {
    const listeners = this.listeners.get(conversationId);
    if (listeners) {
      listeners.forEach((listener) => listener.onAssessmentCompleted?.(chunk));
    }
  }

  notifyStepProgressListeners(conversationId: string, chunk: KomplyanceAiStepProgressChunk) {
    const listeners = this.listeners.get(conversationId);
    if (listeners) {
      listeners.forEach((listener) => listener.onStepProgress?.(chunk));
    }
  }

  /**
   * Subscribe to stream events for a specific conversation ID.
   * Multiple subscribers can listen to the same conversation ID.
   * Returns an unsubscribe function to remove the listener.
   * Note: Components should initialize their state from getAccumulatedMessage/getAccumulatedReferences
   * if there's an active stream when mounting.
   */
  subscribe(conversationId: string, callbacks: StreamCallbacks): () => void {
    const conversationListeners = this.listeners.get(conversationId);
    if (conversationListeners) {
      conversationListeners.push(callbacks);
    } else {
      this.listeners.set(conversationId, [callbacks]);
    }

    // Return cleanup function to unsubscribe
    return () => {
      const currentListeners = this.listeners.get(conversationId);

      this.listeners.set(
        conversationId,
        currentListeners?.filter((listener) => listener !== callbacks) ?? [],
      );
    };
  }

  /**
   * Check if there's an active stream for a conversation ID
   */
  isStreamActive(conversationId: string): boolean {
    const stream = this.streams.get(conversationId);
    return stream?.isActive ?? false;
  }

  /**
   * Get active stream information for a conversation ID
   */
  getStream(conversationId: string): ActiveStream | undefined {
    return this.streams.get(conversationId);
  }

  /**
   * Store the user message that triggered the current stream so it can be
   * restored immediately when navigating back to the conversation.
   */
  setPendingUserMessage(conversationId: string, content: string, createdAt: Date): void {
    this.pendingUserMessages.set(conversationId, { content, createdAt });
  }

  getPendingUserMessage(conversationId: string): { content: string; createdAt: Date } | undefined {
    return this.pendingUserMessages.get(conversationId);
  }

  clearPendingUserMessage(conversationId: string): void {
    this.pendingUserMessages.delete(conversationId);
  }

  /**
   * Clean up stream information for a conversation ID
   */
  cleanupStream(conversationId: string): void {
    this.streams.delete(conversationId);
  }

  /**
   * Get accumulated message content for a conversation ID
   */
  getStreamingMessage(conversationId: string): string {
    const stream = this.streams.get(conversationId);
    return stream?.streamingMessage || '';
  }

  /**
   * Get accumulated references for a conversation ID
   */
  getStreamingReferences(conversationId: string): KomplyanceAiMessageEndRetrieverResource[] {
    const stream = this.streams.get(conversationId);
    return stream?.streamingReferences || [];
  }

  /**
   * Get the abort controller for a conversation ID
   * (API layer will use this to abort the stream)
   */
  getAbortController(conversationId: string): StreamController | undefined {
    const stream = this.streams.get(conversationId);
    return stream?.controller;
  }
}
